import React, { useState } from 'react';
import { X, Link2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { useRoleLabels } from '../../hooks/useRoleLabels';
import EntitySearchField from '../calendar/EntitySearchField';

/**
 * G5E — RelationshipCurateModal.
 * Cura um vínculo contato ↔ empresa|imóvel EXCLUSIVAMENTE via RPC
 * curate_contact_relationship (grava contato_entity_roles + ai_evidence de forma
 * atômica e idempotente por (contato, entity_type, entity_id, role)).
 * HITL: p_decided_by = usuário autenticado (email).
 *
 * Props:
 *  - defaultContato: { id, label } | null  (pré-selecionado a partir do Hub/card)
 *  - defaultEntityType: 'empresa' | 'imovel'
 *  - onSaved: () => void  (chamado após sucesso — refazer a query de origem)
 *  - onClose: () => void
 */
export default function RelationshipCurateModal({
  defaultContato = null,
  defaultEntityType = 'empresa',
  onSaved,
  onClose,
}) {
  const { user } = useAuth();
  const { roles } = useRoleLabels();

  const [contato, setContato] = useState(
    defaultContato ? { type: 'contato', id: defaultContato.id, label: defaultContato.label } : null
  );
  const [entityType, setEntityType] = useState(defaultEntityType === 'imovel' ? 'imovel' : 'empresa');
  const [entity, setEntity] = useState(null);
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const inputCls =
    'w-full text-xs border border-slate-200 rounded px-2 py-1.5 mt-1 focus:outline-none focus:border-blue-400';
  const labelCls = 'text-[10px] uppercase text-slate-500 font-semibold';

  const canSave = Boolean(contato?.id && entity?.id && role && !saving);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('curate_contact_relationship', {
      p_contato_id: contato.id,
      p_entity_type: entityType,
      p_entity_id: entity.id,
      p_role: role,
      p_is_primary: isPrimary,
      p_valid_from: validFrom || null,
      p_valid_to: validTo || null,
      p_notes: notes.trim() || null,
      p_decided_by: user?.email || user?.id || null,
    });
    setSaving(false);
    if (rpcErr) {
      setError(rpcErr.message || String(rpcErr));
      return;
    }
    const action = data?.action;
    const acaoPt = action === 'created' ? 'criado' : action === 'updated' ? 'atualizado' : 'salvo';
    window.alert(`Relacionamento ${acaoPt}.`);
    if (onSaved) onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[10005] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Link2 size={15} className="text-blue-600" /> Curar relacionamento
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          {/* Contato */}
          {defaultContato ? (
            <div>
              <label className={labelCls}>Contato</label>
              <div className="text-xs bg-slate-100 rounded px-2 py-1.5 mt-1 truncate">{defaultContato.label}</div>
            </div>
          ) : (
            <EntitySearchField entityType="contato" value={contato} onChange={setContato} />
          )}

          {/* Tipo de entidade */}
          <div>
            <label className={labelCls}>Tipo de entidade</label>
            <select
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setEntity(null);
              }}
              className={inputCls}
            >
              <option value="empresa">Empresa</option>
              <option value="imovel">Imóvel</option>
            </select>
          </div>

          {/* Entidade (busca) — remonta ao trocar de tipo para limpar o estado interno */}
          <EntitySearchField key={entityType} entityType={entityType} value={entity} onChange={setEntity} />

          {/* Papel */}
          <div>
            <label className={labelCls}>Papel</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
              <option value="">Selecione...</option>
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label_pt}
                </option>
              ))}
            </select>
          </div>

          {/* Principal */}
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
            Relacionamento principal nesta entidade
          </label>

          {/* Vigência */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Início (opcional)</label>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fim (opcional)</label>
              <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className={labelCls}>Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Contexto do vínculo..."
              className={`${inputCls} resize-y`}
            />
          </div>

          {error && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>
          )}

          <button
            onClick={handleSave}
            disabled={!canSave}
            className="w-full px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar relacionamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
